import { getApiConfigNext } from '@y-sports/api-client';
import { AccessApi } from '@y-sports/api-client/generated-next-management/src/apis/AccessApi';
import { AdminApi } from '@y-sports/api-client/generated-next-management/src/apis/AdminApi';
import type {
  PlanEntitlementResponse,
  PlanResponse,
  PlanUpdateRequest,
  UserAccessLimitResponse,
  UserAccessLimitsResponse,
  UserAccessRelationshipsResponse,
  UserAccessResponse,
  UserEntitlementResponse,
  UserIdentityResponse,
  UserPlanResponse,
} from '@y-sports/api-client/generated-next-management/src/models';
import {
  Configuration as NextManagementConfiguration,
  ResponseError,
} from '@y-sports/api-client/generated-next-management/src/runtime';

export type UserAccessIdentity = UserIdentityResponse;
export type UserAccessRelationships = UserAccessRelationshipsResponse;
export type UserAccessLimit = UserAccessLimitResponse;
export type UserAccessLimits = UserAccessLimitsResponse;
export type UserAccessEntitlement = UserEntitlementResponse;
export type UserPlan = UserPlanResponse;
export type PlanEntitlement = PlanEntitlementResponse;
export type AccessPlan = PlanResponse;
export type PlanUpdatePayload = PlanUpdateRequest;

export interface ResourcePermissions {
  canView?: boolean | null;
  canManage?: boolean | null;
  canEdit?: boolean | null;
  canOperate?: boolean | null;
  canViewSensitiveFields?: boolean | null;
  accessLevel?: string | null;
}

export interface UserAccess {
  identity: UserAccessIdentity;
  globalRoles: string[];
  effectiveRoles: string[];
  capabilities: string[];
  currentPlan: UserPlan;
  relationships: UserAccessRelationships;
  limits: UserAccessLimits;
  entitlements: UserAccessEntitlement[];
}

export const hasCapability = (access: UserAccess | undefined, capability: string): boolean =>
  Boolean(access?.capabilities?.includes(capability));

const getAccessApi = async () => {
  const apiConfig = await getApiConfigNext();
  return new AccessApi(new NextManagementConfiguration({
    basePath: apiConfig.basePath,
    headers: apiConfig.headers,
  }));
};

const getAdminApi = async () => {
  const apiConfig = await getApiConfigNext();
  return new AdminApi(new NextManagementConfiguration({
    basePath: apiConfig.basePath,
    headers: apiConfig.headers,
  }));
};

function normalizeAccess(payload: Partial<UserAccess>): UserAccess {
  return {
    identity: payload.identity ?? {},
    globalRoles: payload.globalRoles ?? [],
    effectiveRoles: payload.effectiveRoles ?? [],
    capabilities: payload.capabilities ?? [],
    currentPlan: payload.currentPlan ?? {
      code: 'FREE',
      name: 'Free',
      billingInterval: 'NONE',
      priceCents: 0,
      currency: 'USD',
      source: 'DEFAULT',
      active: true,
      defaultPlan: true,
    },
    relationships: payload.relationships ?? {
      managedTeams: 0,
      assignedPeople: 0,
      playerAssignments: 0,
      ownedTournaments: 0,
      ownedLeagues: 0,
      ownedTrainings: 0,
      claimedTrainingPlayers: 0,
      matchedPeopleByEmail: 0,
    },
    limits: payload.limits ?? {
      activeTournaments: { key: 'tournament.active.limit', used: 0, allowed: true },
      activeLeagues: { key: 'league.active.limit', used: 0, allowed: true },
      activeTrainings: { key: 'training.active.limit', used: 0, allowed: true },
      managedTeams: { key: 'team.managed.limit', used: 0, allowed: true },
    },
    entitlements: payload.entitlements ?? [],
  };
}

async function parseError(response: Response, fallback: string) {
  try {
    const payload = await response.json();
    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
  } catch (_error) {
    // Fall through to the generic message.
  }
  return fallback;
}

async function normalizeApiError(error: unknown, fallback: string) {
  if (error instanceof ResponseError) {
    return new Error(await parseError(error.response, fallback));
  }
  return error;
}

export const AccessDataService = {
  getCurrentAccess: async (): Promise<UserAccess> => {
    const accessApi = await getAccessApi();
    return normalizeAccess(await accessApi.getMyAccess());
  },

  redeemPromoCode: async (code: string): Promise<UserAccess> => {
    try {
      const accessApi = await getAccessApi();
      return normalizeAccess(await accessApi.redeemMyPromoCode({
        promoCodeRedemptionRequest: { code },
      }));
    } catch (error) {
      throw await normalizeApiError(error, 'Failed to redeem promo code');
    }
  },

  getPlans: async (): Promise<AccessPlan[]> => {
    const accessApi = await getAccessApi();
    return accessApi.listMyPlans();
  },

  getAdminPlans: async (): Promise<AccessPlan[]> => {
    const adminApi = await getAdminApi();
    return adminApi.listAdminPlans();
  },

  selectPlan: async (planCode: string): Promise<UserAccess> => {
    try {
      const accessApi = await getAccessApi();
      return normalizeAccess(await accessApi.selectMyPlan({
        planSelectionRequest: { planCode },
      }));
    } catch (error) {
      throw await normalizeApiError(error, 'Failed to select plan');
    }
  },

  updatePlan: async (planCode: string, payload: PlanUpdatePayload): Promise<AccessPlan> => {
    try {
      const adminApi = await getAdminApi();
      return adminApi.updateAdminPlan({
        code: planCode,
        planUpdateRequest: payload,
      });
    } catch (error) {
      throw await normalizeApiError(error, 'Failed to update plan');
    }
  },
};
